import { FC, useCallback, useMemo } from 'react';
import { CellContext, Row } from '@tanstack/react-table';
import { Select } from 'antd';
import { NoContentItem } from '../../../components';
import { getRowPathIndexes } from './utils';
import { BaseOptionType } from 'rc-select/lib/Select';
import { TableRowPositionChangeHandler } from './types';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './RowParentIdSelect.translations.json';
import { UUIDType } from '../../../utils';

const { addTranslations, useTranslation } = i18nNsFactory('row-parent-select');
addTranslations(translations);

interface RowParentSelectOption extends BaseOptionType {
  row: Row<any> | null;
}

export interface RowParentIdSelectProps extends CellContext<any, any> {
  id: string;
  onSelect?: TableRowPositionChangeHandler;
}

export const RowParentIdSelect: FC<RowParentIdSelectProps> = (props) => {
  const { row, table, onSelect, id } = props;
  const { t } = useTranslation();
  const { flatRows } = table.getCoreRowModel();

  const options = useMemo(
    () =>
      [
        {
          value: 'root',
          label: t('root-level'),
          row: null,
        } as RowParentSelectOption,
      ].concat(
        flatRows
          .filter(
            (r) =>
              r !== row &&
              r.getParentRows().every((parentRow) => parentRow !== row)
          )
          .map((r) => ({
            value: r.id,
            label: `${r.original.displayName}`,
            row: r,
          }))
      ),
    [flatRows, row]
  );

  const handleSelect = useCallback(
    (
      selectedValue: string,
      selectedOptions: RowParentSelectOption | RowParentSelectOption[]
    ) => {
      const { row: selectedRow } = Array.isArray(selectedOptions)
        ? selectedOptions[0]
        : selectedOptions;
      const childIndex =
        (selectedRow ? selectedRow?.subRows : table.options.data)?.length || 0;
      const toParentId = (selectedRow?.id as UUIDType) || null;
      const toDepthIndex = toParentId
        ? (table.getRow(toParentId)?.depth || 0) + 1
        : 0;

      onSelect?.(
        getRowPathIndexes(row),
        (selectedRow ? getRowPathIndexes(selectedRow) : []).concat(childIndex),
        row.id as UUIDType,
        (row.parentId as UUIDType) || null,
        toParentId,
        toDepthIndex
      );

      selectedRow
        ?.getParentRows()
        .concat(selectedRow)
        .forEach((r) => r.toggleExpanded(true));
    },
    [onSelect, row]
  );

  return (
    <Select<string, RowParentSelectOption>
      id={id}
      value={row.getParentRow()?.id || 'root'}
      onSelect={handleSelect}
      options={options}
      styles={{
        popup: { root: { width: 'max-content' } },
      }}
      size="small"
      showSearch
      filterOption={(input, option) =>
        option?.label.toLowerCase().includes(input.toLowerCase())
      }
      notFoundContent={<NoContentItem />}
      autoFocus
      defaultOpen
    />
  );
};
